import { Divider, Menu, MenuItem, MenuProps, useTheme } from '@mui/material';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { SxProps, Theme } from '@mui/system';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { usersProvider } from 'providers/contexts/extensions/Users';
import { useCosmos } from 'providers/contexts/CosmosContext';
import React, {
  Fragment,
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from 'react-admin';
import { bloxValues } from 'themes/constants';
import { SimpleConfirmDeleteModal } from 'components/Modals/SimpleConfirmDeleteModal';
import { useSearchItemsSafe } from 'components/Search/components/SearchItemsProvider';
import { BaseMiniMenuProps } from './MiniMenuSelector';

export type MuiCloseEvent = Partial<Parameters<NonNullable<MenuProps['onClose']>>[0]>;
export type MuiCloseReasons = Partial<Parameters<NonNullable<MenuProps['onClose']>>[1]>;

export type GroupsMiniMenuConfig = {
  edit?: { enabled?: boolean };
  delete?: { enabled?: boolean };
  clearSelections?: { enabled?: boolean };
};

export const allGroupsMiniMenuOptions: GroupsMiniMenuConfig = {
  edit: { enabled: true },
  delete: { enabled: true },
  clearSelections: { enabled: true },
};

export type GroupSearchMenuItem = {
  id: string | number;
  name?: string;
  members?: number;
  [key: string]: any;
};

export interface GroupsMiniMenuProps extends Omit<
  BaseMiniMenuProps,
  'contextMenuItem' | 'selectedItems'
> {
  selectedItems: GroupSearchMenuItem[];
  menuConfig?: GroupsMiniMenuConfig;
  contextMenuItem?: GroupSearchMenuItem | null;
}

type ButtonState = {
  disabled: boolean;
  show: boolean;
};

export const GroupsMiniMenu = memo(
  forwardRef<HTMLDivElement, GroupsMiniMenuProps>(
    (
      {
        menuEvent,
        handleMiniMenuClose,
        clearSelectedItems,
        menuConfig = allGroupsMiniMenuOptions,
        contextMenuItem,
        anchorEl,
        selectedItems,
      },
      ref,
    ) => {
      const themes = useTheme();
      const notify = useNotify();
      const { t } = useTranslation();
      const { activeSite } = useCosmos();
      const { queryClient } = useCosmosQueryClient();
      const searchCtx = useSearchItemsSafe();

      const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
      const [isDeleting, setIsDeleting] = useState(false);

      const isButtonMode = Boolean(anchorEl);
      const menuX = menuEvent?.clientX;
      const menuY = menuEvent?.clientY;
      const hasXY = typeof menuX === 'number' && typeof menuY === 'number';
      const isOpen = isButtonMode ? Boolean(anchorEl) : hasXY;
      const anchorPos: MenuProps['anchorPosition'] = hasXY
        ? { top: menuY, left: menuX }
        : undefined;

      // Effective items: multi-select aware context logic
      const hasSelections = Boolean(selectedItems?.length);
      const contextItemId = contextMenuItem?.id;
      const isContextItemSelected = useMemo(
        () =>
          hasSelections && contextItemId !== undefined
            ? selectedItems.some((item) => String(item?.id) === String(contextItemId))
            : false,
        [hasSelections, contextItemId, selectedItems],
      );

      // 1. Right-clicked item is part of the selection → operate on all selected
      // 2. Right-clicked item is NOT in the selection → operate only on that item
      // 3. Nothing selected → operate only on the right-clicked item
      const effectiveItems = useMemo(
        () =>
          hasSelections && isContextItemSelected
            ? selectedItems
            : contextMenuItem
              ? [contextMenuItem]
              : [],
        [hasSelections, isContextItemSelected, selectedItems, contextMenuItem],
      );

      const effectiveCount = effectiveItems.length;

      const confirmDeleteText = useMemo(
        () =>
          effectiveCount === 1
            ? t('blox.general.will_delete_one', '{{count}} item will be deleted.', {
                count: 1,
              })
            : t('blox.general.will_delete', '{{count}} items will be deleted.', {
                count: effectiveCount,
              }),
        [effectiveCount, t],
      );

      const iconSx: SxProps<Theme> = useMemo(() => {
        return {
          fontSize: bloxValues.iconSizeRegular,
          color: themes.palette.text.secondary,
          mr: 1,
        };
      }, [themes]);

      const menuSx: SxProps<Theme> = useMemo(() => {
        return {
          '& .MuiMenuItem-root': {
            fontSize: bloxValues.regularFontSizeRem,
          },
        };
      }, []);

      const menuListRef = useRef<HTMLUListElement>(null);

      const internalOnClose = useCallback(
        (event?: MuiCloseEvent, reason?: MuiCloseReasons) => {
          handleMiniMenuClose();
        },
        [handleMiniMenuClose],
      );

      const handleMenuClickCaptures = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          if (!menuListRef?.current?.contains(event?.target as Node)) {
            event.stopPropagation();
            event.preventDefault();
            internalOnClose(event, 'backdropClick');
          }
        },
        [internalOnClose],
      );

      // Button states
      const editState: ButtonState = useMemo(() => {
        return {
          // Edit is only valid for a single item
          disabled: effectiveCount !== 1,
          show: Boolean(menuConfig?.edit?.enabled),
        };
      }, [effectiveCount, menuConfig?.edit?.enabled]);

      const deleteState: ButtonState = useMemo(() => {
        return {
          disabled: effectiveCount === 0 || isDeleting,
          show: Boolean(menuConfig?.delete?.enabled),
        };
      }, [effectiveCount, isDeleting, menuConfig?.delete?.enabled]);

      const clearButtonState: ButtonState = useMemo(() => {
        return {
          show: Boolean(menuConfig?.clearSelections?.enabled),
          disabled: !hasSelections,
        };
      }, [menuConfig?.clearSelections?.enabled, hasSelections]);

      const hasOptionsAboveClear = editState.show || deleteState.show;

      // Edit
      const handleOpenEdit = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event.preventDefault();
          if (editState.disabled || effectiveItems.length !== 1) {
            return;
          }
          const item = effectiveItems[0];
          window.open(
            `/${activeSite}/settings/admins/groups/edit/${item.id}`,
            '_blank',
            'noopener,noreferrer',
          );
          internalOnClose();
        },
        [activeSite, editState.disabled, effectiveItems, internalOnClose],
      );

      // Delete
      const openDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(true);
        internalOnClose();
      }, [internalOnClose]);

      const closeDeleteDialog = useCallback(() => {
        setConfirmDeleteOpen(false);
      }, []);

      const handleDeleteMenuClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event.preventDefault();
          if (deleteState.disabled || isDeleting) {
            return;
          }
          openDeleteDialog();
        },
        [deleteState.disabled, isDeleting, openDeleteDialog],
      );

      const handleDeleteConfirm = useCallback(async () => {
        if (effectiveItems.length === 0 || isDeleting) {
          closeDeleteDialog();
          return;
        }
        setIsDeleting(true);
        try {
          await Promise.all(
            effectiveItems.map((item) => usersProvider.deleteGroupById(item.id)),
          );
          await Promise.all([queryClient.invalidateQueries({ queryKey: ['group'] })]);
          // Clear selection before refetch so deleted items are not treated as "hidden selected" by the DataGrid selection model handler when new data arrives.
          clearSelectedItems();
          await searchCtx?.refetch?.();
          notify(
            effectiveItems.length > 1
              ? t(
                  'blox.user.groups.form.delete.success_multiple',
                  '{{count}} groups deleted',
                  { count: effectiveItems.length },
                )
              : t('blox.user.groups.form.delete.success', 'Group deleted'),
            { type: 'success' },
          );
        } catch (error: any) {
          notify(
            error?.message ||
              t('blox.user.groups.form.delete.error', 'Error deleting group'),
            { type: 'error' },
          );
        } finally {
          setIsDeleting(false);
          closeDeleteDialog();
        }
      }, [
        effectiveItems,
        isDeleting,
        queryClient,
        searchCtx,
        notify,
        t,
        clearSelectedItems,
        closeDeleteDialog,
      ]);

      const handleDeleteCancel = useCallback(() => {
        closeDeleteDialog();
      }, [closeDeleteDialog]);

      // Clear selections
      const handleClearSelections = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
          event.preventDefault();
          clearSelectedItems();
          internalOnClose();
        },
        [clearSelectedItems, internalOnClose],
      );

      return (
        <Fragment>
          <SimpleConfirmDeleteModal
            open={confirmDeleteOpen}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            subTxt={confirmDeleteText}
            initialWidth={350}
            initialHeight={215}
            isDraggable={true}
          />
          <Menu
            ref={ref}
            data-testid="groups-minimenu"
            disableEnforceFocus={true}
            open={isOpen}
            onClose={internalOnClose}
            sx={menuSx}
            hideBackdrop={true}
            onMouseDownCapture={handleMenuClickCaptures}
            onContextMenuCapture={handleMenuClickCaptures}
            {...(isButtonMode
              ? { anchorEl }
              : {
                  anchorReference: 'anchorPosition' as const,
                  anchorPosition: anchorPos,
                  TransitionProps: { timeout: { enter: 0, exit: 0 } },
                })}
            slotProps={{
              list: {
                autoFocusItem: true,
                ref: menuListRef,
              },
            }}
          >
            {editState.show ? (
              <MenuItem
                data-testid="groups-minimenu-edit"
                disabled={editState.disabled}
                onClick={handleOpenEdit}
              >
                <EditIcon sx={iconSx} />
                {t('blox.actions.edit')}
              </MenuItem>
            ) : null}

            {deleteState.show ? (
              <MenuItem
                data-testid="groups-minimenu-delete"
                onClick={handleDeleteMenuClick}
                disabled={deleteState.disabled}
              >
                <DeleteForeverIcon sx={iconSx} />
                {t('blox.actions.delete')}
              </MenuItem>
            ) : null}

            {/* Divider only when there are action items above */}
            {clearButtonState.show && hasOptionsAboveClear ? <Divider /> : null}

            {clearButtonState.show ? (
              <MenuItem
                id="groups-minimenu-clear-selections"
                data-testid="groups-minimenu-clear-selections"
                disabled={clearButtonState.disabled}
                onClick={handleClearSelections}
              >
                <ClearAllIcon sx={iconSx} />
                {t('blox.batch_edit.actions.clear')}
              </MenuItem>
            ) : null}
          </Menu>
        </Fragment>
      );
    },
  ),
);

GroupsMiniMenu.displayName = 'GroupsMiniMenu';
